import {
  Menu,
  MenuItem,
  MenuItemText,
  SideNavigation,
  SideNavigationHeader,
  SideNavigationMenuItem,
  SideNavigationMenuList,
} from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  REACT_APP_AUTH_ROLE_ANALYST,
  REACT_APP_AUTH_ROLE_COLLECTOR,
  REACT_APP_AUTH_ROLE_OPERATOR,
} from '../../AuthProvider';
import { alphaToggleAtom } from '../../atoms/alpha';
import {
  activeNavItemAtom,
  activeNavSubItemAtom,
  isMenuOpenAtom,
  isNavExpandedAtom,
} from '../../atoms/navigation';
import { THEME_TYPES, toggleDarkModeAtom } from '../../atoms/theme';
import { Logo, LogoText } from '../../components/Logo/Logo';
import Secured from '../../components/Secured';
import { useAuthDetails } from '../../utils/auth';

const StyledLink = styled(Link)`
  width: 100%;
`;

export function Navigation() {
  const intl = useIntl();
  const { handleLogout } = useAuthDetails();
  const location = useLocation();

  const [themeType, themeToggle] = useAtom(toggleDarkModeAtom);

  const isAlphaEnabled = useAtomValue(alphaToggleAtom);

  const theme = useTheme();
  const isTouch = !theme.mqMatch(theme.mqFrom('md'));

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useAtom(isMenuOpenAtom);
  const [isOpen, setIsOpen] = useAtom(isNavExpandedAtom);
  const [selectedSubmenu, setSelectedSubmenu] = useAtom(activeNavItemAtom);
  const [selectedSubmenuItem, setSelectedSubmenuItem] =
    useAtom(activeNavSubItemAtom);

  const onItemClick = ({ isSubmenuItem = false, sectionName }) => {
    if (isTouch && isSubmenuItem) {
      setSelectedSubmenu(null);
      setIsMobileMenuOpen(false);
    } else if (isTouch && selectedSubmenu === sectionName) {
      setSelectedSubmenu(null);
    } else if (isTouch) {
      setSelectedSubmenu(sectionName);
    }
  };

  const ALL_INTERNAL_ROLES = [
    REACT_APP_AUTH_ROLE_ANALYST,
    REACT_APP_AUTH_ROLE_OPERATOR,
    REACT_APP_AUTH_ROLE_COLLECTOR,
  ];
  const ANALYSTS_AND_OPERATORS = [
    REACT_APP_AUTH_ROLE_ANALYST,
    REACT_APP_AUTH_ROLE_OPERATOR,
  ];
  const OPERATORS_ONLY = [REACT_APP_AUTH_ROLE_OPERATOR];

  const sections = [
    {
      allowed: [...ANALYSTS_AND_OPERATORS],
      icon: 'AccountSettings',
      name: intl.formatMessage({
        defaultMessage: 'My Videos',
        id: 'bb7e3',
      }),
      subSections: [
        {
          allowed: [...ANALYSTS_AND_OPERATORS],
          name: intl.formatMessage({
            defaultMessage: 'Clips',
            id: 'e913c',
          }),
          route: '/my-cutups',
        },
        {
          allowed: [...ANALYSTS_AND_OPERATORS],
          name: intl.formatMessage({
            defaultMessage: 'Playlists',
            id: '008e1',
          }),
          route: '/my-playlists-basic',
        },
      ],
    },
    {
      allowed: [...OPERATORS_ONLY],
      icon: 'VideoAvailable',
      name: intl.formatMessage({
        defaultMessage: 'Video Alignment',
        id: '17e65',
      }),
      subSections: [
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Alignment Queue',
            id: '76410',
          }),
          route: '/requires-offsets',
        },
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Alignment Verification',
            id: 'f0a3d',
          }),
          route: '/alignment-verification',
        },
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Alignment Issues',
            id: '6cab4',
          }),
          route: '/alignment-issues',
        },
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Historical Competitions',
            id: 'fe00b',
          }),
          route: '/historical-competitions',
        },
      ],
    },
    {
      allowed: [...OPERATORS_ONLY],
      icon: 'Upload',
      name: intl.formatMessage({
        defaultMessage: 'Upload',
        id: '91412',
      }),
      subSections: [
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Upload',
            id: '91412',
          }),
          route: '/upload',
        },
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Awaiting Approval',
            id: 'af655',
          }),
          route: '/approve',
        },
      ],
    },
    {
      allowed: [...ALL_INTERNAL_ROLES],
      icon: 'Camera',
      name: intl.formatMessage({
        defaultMessage: 'Player',
        id: '636da',
      }),
      subSections: [
        {
          allowed: [...ALL_INTERNAL_ROLES],
          name: intl.formatMessage({
            defaultMessage: 'Player',
            id: '636da',
          }),
          route: '/player-basic',
        },
      ],
    },
    {
      allowed: [...ALL_INTERNAL_ROLES],
      icon: 'Download',
      name: intl.formatMessage({
        defaultMessage: 'Download',
        id: '801ab',
      }),
      subSections: [
        {
          allowed: [...ALL_INTERNAL_ROLES],
          name: intl.formatMessage({
            defaultMessage: 'Download',
            id: '801ab',
          }),
          route: '/download',
        },
      ],
    },
    {
      allowed: [...OPERATORS_ONLY],
      icon: 'Video',
      name: intl.formatMessage({
        defaultMessage: 'Streams',
        id: '21b3c',
      }),
      subSections: [
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Channels',
            id: '14cb4',
          }),
          route: '/aws-streams',
        },
      ],
    },
    {
      allowed: [...OPERATORS_ONLY],
      icon: 'Edit',
      name: intl.formatMessage({
        defaultMessage: 'Edit',
        id: '7dce1',
      }),
      subSections: [
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Job Status',
            id: '093e2',
          }),
          route: '/edits',
        },
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Trim',
            id: '1fc58',
          }),
          route: '/trim',
        },
      ],
    },
    {
      allowed: [...OPERATORS_ONLY],
      icon: 'PlayerMarkers',
      isAlphaTest: true,
      name: intl.formatMessage({
        defaultMessage: 'Extract',
        id: '97478',
      }),
      subSections: [
        {
          allowed: [...OPERATORS_ONLY],
          name: intl.formatMessage({
            defaultMessage: 'Frames',
            id: '2b81b',
          }),
          route: '/frame-extraction',
        },
      ],
    },
  ];

  const rootOfUrl = (route) => route.split('/')[1];
  const findSectionToMakeActive = (pathname) =>
    sections.find((section) =>
      section.subSections
        .map((subSection) => rootOfUrl(subSection.route))
        .includes(rootOfUrl(pathname)),
    );

  useEffect(() => {
    const activeSection = findSectionToMakeActive(location.pathname);
    setSelectedSubmenu(activeSection?.name || null);
  }, [window.location.pathname]);

  const handleHeaderClick = () => setIsOpen(!isOpen);

  const handleLinkClick = ({ submenuItem }) => {
    setSelectedSubmenuItem(submenuItem);
    setIsOpen(false);
  };

  return (
    <Secured>
      <SideNavigation isOpen={isOpen} isTouch={isTouch}>
        <SideNavigationHeader
          isOpen={isOpen}
          isTouch={isTouch}
          logoIcon={<Logo />}
          logoText={<LogoText />}
          onClick={isTouch ? handleHeaderClick : undefined}
        />
        <SideNavigationMenuList selectedSubMenu={selectedSubmenu}>
          {sections.map((section) =>
            !section.isAlphaTest || (section.isAlphaTest && isAlphaEnabled) ? (
              <Secured key={section.name} allowed={section.allowed}>
                <SideNavigationMenuItem
                  menuItemId={section.name}
                  icon={section.icon}
                  isSelected={selectedSubmenu === section.name}
                  selectedSubmenu={selectedSubmenu}
                  componentProps={{
                    onClick: (params) =>
                      onItemClick({
                        ...params,
                        sectionName: section.name,
                      }),
                  }}
                  isSubmenuOpen={isOpen && selectedSubmenu === section.name}
                  isOpen={isMobileMenuOpen}
                  subMenu={
                    <Menu variant="subnavigation">
                      {section.subSections.map((subSection) =>
                        !subSection.isAlphaTest ||
                        (subSection.isAlphaTest && isAlphaEnabled) ? (
                          <Secured
                            key={`${section.name}-${subSection.name}`}
                            allowed={subSection.allowed}
                          >
                            <MenuItem
                              isActive={selectedSubmenuItem === subSection.name}
                              variant="subnavigation"
                              size={isTouch ? 'touch' : 'small'}
                              opacity="secondary"
                            >
                              <StyledLink
                                onClick={() =>
                                  handleLinkClick({
                                    submenuItem: subSection.name,
                                  })
                                }
                                to={subSection.route}
                              >
                                <MenuItemText isTouch={isTouch}>
                                  {subSection.name}
                                </MenuItemText>
                              </StyledLink>
                            </MenuItem>
                          </Secured>
                        ) : (
                          <></>
                        ),
                      )}
                    </Menu>
                  }
                >
                  {section.name}
                </SideNavigationMenuItem>
              </Secured>
            ) : (
              <></>
            ),
          )}
        </SideNavigationMenuList>
        {(!isTouch || (isTouch && !selectedSubmenu)) && (
          <>
            <SideNavigationMenuItem
              icon="Settings"
              componentProps={{
                onClick: themeToggle,
              }}
            >
              {themeType === THEME_TYPES.DARK ? (
                <FormattedMessage defaultMessage="Light mode" id="6c3e8" />
              ) : (
                <FormattedMessage defaultMessage="Dark mode" id="3924c" />
              )}
            </SideNavigationMenuItem>
            <SideNavigationMenuItem
              icon="LogOut"
              componentProps={{
                onClick: handleLogout,
              }}
            >
              <FormattedMessage defaultMessage="Logout" id="0323d" />
            </SideNavigationMenuItem>
          </>
        )}
      </SideNavigation>
    </Secured>
  );
}

export default { Navigation };
